import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// Use environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddStockAdjustment() {
  const [location, setLocation] = useState("");
  const [reason, setReason] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [fromLocations, setFromLocations] = useState([]);
  const [stockAdjustmentDate, setStockAdjustmentDate] = useState(new Date().toISOString().split("T")[0]); // Default to today
  const [adjustmentItems, setAdjustmentItems] = useState([{ product_id: "", qty: 1 }]);
  const [products, setProducts] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [categories, setCategories] = useState([]);
  const [adjustmentType, setAdjustmentType] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdjustmentCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("No token found. Please log in.");
                return;
            }       

            const response = await axios.get(`${API_BASE_URL}/adjustmentTypes`, {   
                headers: { Authorization: `Bearer ${token}` },
            });

            setCategories(response.data.adjustmentCategories || []);
            console.log("Categories: ", response.data.adjustmentCategories);
        } catch (error) {
            console.error("Error fetching categories:", error);
            toast.error("Error fetching adjustment categories");
        }
    } ;

    fetchAdjustmentCategories();
  }, []);

  const handleAdjustmentTypeChange = (name) => {
    setAdjustmentType((prev) =>
        prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/users/specific`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const userData = response.data;
        setCurrentUser(userData);

        // Fetch locations and products based on role
        if (userData.role === "Main Admin") {
          fetchAllLocations();
          fetchAllProducts();
        } else if (userData.role === "Company Admin") {
          fetchFilteredLocations(userData);
          fetchFilteredProducts(userData);
        }else if (userData.role === "Farm User" || userData.role === "Outlet User") {
          fetchFilteredLocations(userData);
          fetchFilteredProducts(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };

    fetchCurrentUser();
  }, []);

  const fetchAllLocations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/locations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (currentUser.role === "Farm User" || currentUser.role === "Outlet User") {
      setFromLocations(response.data.fromLocations || []); // Only assigned locations
    } else {
      setFromLocations(response.data.fromLocations); // Use fetched locations for Admin
    }
  };

  const fetchAllProducts = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/products`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setProducts(response.data.products);
  };

  const fetchFilteredLocations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/products/filtered-to-from-locations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (currentUser.role === "Farm User" || currentUser.role === "Outlet User") {
      setFromLocations(response.data.fromLocations || []); // Only assigned locations
    } else {
      setFromLocations(response.data.fromLocations); // Use fetched locations for Admin
    }
  };

  const fetchFilteredProducts = async (userData, locationId) => {
    if (!locationId) {
      // toast.error("Location ID is missing.");
      return;
    }
  
    const token = localStorage.getItem("token");
  
    try {
      const response = await axios.get(
        `${API_BASE_URL}/products/filtered-products-by-location?organization=${userData.organization._id}&locationId=${locationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      if (response.data.products.length === 0) {
        // No products available for this location
        toast.error("No products available in this location.");
        setProducts([]);  // Clear the previously loaded products
      } else {
        setProducts(response.data.products); // Set products in state
      }
    } catch (error) {
      console.error("Error fetching filtered products:", error);
      toast.error("There are no production batches in this location.");
      setProducts([]); // Clear the products in case of error
    }
  };
  

  useEffect(() => {
    // Clear products whenever location changes
    setProducts([]);
    if (currentUser && location) {
      fetchFilteredProducts(currentUser, location);
    }
  }, [location, currentUser]);

  useEffect(() => {
  // Assuming `products` is the state that stores the product data from the API
  console.log(products); // Add this line to check the data
}, [products]);
  
  const handleQuantityChange = (productId, newQty) => {
    setAdjustmentItems((prevItems) =>
      prevItems.map((item) => {
        if (item.product_id === productId) {
          const product = products.find((prod) => prod._id === productId);

          if (!product) return item; // Skip if product not found

          const isPartialQuantityAllowed = product.partial_qty_allocation;

          // Prevent decimals if partial allocation is false
          if (!isPartialQuantityAllowed && !Number.isInteger(newQty)) {
            toast.error(`Quantity for ${product.product_name} must be a whole number.`);
            return item; // Keep previous value
          }

          return { ...item, qty: newQty };
        }
        return item;
      })
    );
  };

  // const handleAddAdjustmentItem = () => {
  //   setAdjustmentItems([...adjustmentItems, { product_id: "", qty: 1 }]);
  // };

  // const handleRemoveAdjustmentItem = (index) => {
  //   setAdjustmentItems(adjustmentItems.filter((_, i) => i !== index));
  // };

  const handleAdjustmentItemChange = (index, field, value) => {
    const updatedItems = [...adjustmentItems];
    updatedItems[index][field] = value;
    setAdjustmentItems(updatedItems);
  };

  //submit function
  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast.error("Please select an adjustment category.");
      return;
    }
  
    const categoryDetails = categories.find(category => category._id === selectedCategory);
    if (!categoryDetails) {
      toast.error("Invalid adjustment category.");
      return;
    }
  
    const isRemovalCategory = categoryDetails.stock_movement_type === "Remove";
  
    if (adjustmentItems.some((item) => !item.product_id || item.qty <= 0)) {
      toast.error("Please provide valid product and quantity for all items.");
      return;
    }
  
    for (const item of adjustmentItems) {
      const product = products.find((prod) => prod._id === item.product_id);
  
      if (!product || product.updated_stock_qty === undefined) {
        toast.error(`Stock information unavailable for a selected product.`);
        return;
      }
  
      if (isRemovalCategory) {
        const availableStock = product.updated_stock_qty;
        if (item.qty > availableStock) {
          toast.error(`Selected quantity for ${product.product_name} exceeds available stock of ${availableStock}.`);
          return;
        }
      }
  
      if (product) {
        const isPartialQuantityAllowed = product.partial_qty_allocation;
        if (!isPartialQuantityAllowed && !Number.isInteger(item.qty)) {
          toast.error(`Quantity for ${product.product_name} must be a whole number.`);
          return;
        }
      }
    }
  
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token is missing. Please log in again.");
      return;
    }
  
    try {
      const payload = {
        location_id: location,
        stock_adjustment_date: stockAdjustmentDate,
        stockAdjustmentItems: adjustmentItems,
        stockAdjustmentType: selectedCategory,
        organization: currentUser?.organization?._id,
        created_by: currentUser._id,
        reason: reason,
      };
  
      console.log("Sending stock adjustment data:", payload);
      const response = await axios.post(`${API_BASE_URL}/stockAdjustments/add`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      console.log("Stock adjustment response:", response.data);

      if (response.status === 201 && response.data?.stockAdjustment?._id) {
        const adjustmentId = response.data.stockAdjustment._id; // Get newly created adjustment _id
  
        toast.success(response.data.message || "Stock Adjustment created successfully!");
  
        // Record Stock Movements
        for (const item of adjustmentItems) {
          const stockMovement = {
            product_id: item.product_id,
            location_id: location,
            movement_qty: Number(item.qty),
            movement_type: categoryDetails.stock_movement_type,
            movement_type_id: adjustmentId,
            movement_name: "adjustments",
            organization_id: currentUser?.organization?._id,
            performed_by: currentUser._id,
            user_role: currentUser.role,
          };
  
          console.log("Sending stock movement data:", stockMovement);
          await axios.post(`${API_BASE_URL}/transferOrders/stockmovement`, stockMovement, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
  
          console.log("Stock Movement recorded successfully for:", item.product_id);
        }
  
        // Reset form state
        setLocation("");
        setSelectedCategory("");
        setAdjustmentItems([{ product_id: "", qty: 0, product_name: "" }]);
        setProducts([]);
        setReason("");
      } else {
        toast.error("Stock adjustment failed.");
      }
    } catch (error) {
      console.error("Error processing request:", error.response?.data || error.message);
      toast.error("Stock movement or adjustment failed. Please try again.");
    }
  };    

  const ProductOption = ({ data, innerRef, innerProps, isFocused, isSelected, index }) => {
    const currentCategory = data.category;
  
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`flex items-center p-2 ${isFocused ? "bg-gray-200" : ""} ${isSelected ? "bg-blue-500 text-white" : ""}`}
      >
        {data.product_image && (
          <img
            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${data.product_image}`}
            alt={data.product_name}
            className="w-10 h-10 object-cover mr-2"
          />
        )}
        <span>{data.product_name}</span>
  
        {/* Add a divider before a new category */}
        {index !== 0 && currentCategory !== data.category && (
          <div className="border-t my-2"></div>
        )}
      </div>
    );
  };
  
  const customProductSelect = (productOptions, index) => {
    // Extract selected product IDs, excluding the current row's product to allow reselection
    const selectedProductIds = adjustmentItems
      .filter((_, i) => i !== index)
      .map((item) => item.product_id);
  
    // Filter the product options to exclude already selected products
    const filteredOptions = productOptions.filter(
      (product) => !selectedProductIds.includes(product._id)
    );

  
    return (
      <div>
          <Select
            options={filteredOptions}
            getOptionLabel={(e) => e.product_name}
            getOptionValue={(e) => e._id}
            components={{ Option: (props) => <ProductOption {...props} index={index} /> }}
            onChange={(selectedOption) =>
              handleAdjustmentItemChange(index, "product_id", selectedOption ? selectedOption._id : "")
            }
            placeholder="Search and select product"
            className="w-full"
            value={adjustmentItems[index]?.product_id ? filteredOptions.find((option) => option._id === adjustmentItems[index].product_id) : null}
          />
      </div>
    );
  };
    
  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-blue-700 mb-6">Add Stock Adjustment</h2>
        <div className="grid grid-cols-2 gap-8">
          {/* From Location */}
          <div className="mb-4">
          <label className="block text-gray-700 mb-2">Location</label>
          <select
            className="w-full p-3 border rounded-lg"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            >
            <option value="" disabled>Select Location</option>
            {fromLocations
                .filter((location) => location._id !== toLocation) // Exclude selected "To Location"
                .map((location) => (
                <option key={location._id} value={location._id}>
                    {location.location_name}
                </option>
                ))}
            </select>
        </div>
        <div className="mb-4">
              <label className="w-full block text-gray-700 mb-2">
                Stock Adjustment Type
              </label>
              <select
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Select Adjustment</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.adjustment_category_name}
                  </option>
                ))}
              </select>
            </div>
      </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Items</h3>
          {/* Column Headers */}
          <div className="grid grid-cols-2 gap-8 mb-1 block text-gray-700 ">
            <div>Product</div>
            <div className="grid grid-cols-2 gap-8 mb-2 block text-gray-700">
              <div>Quantity</div>
              {/* <div>Action</div> */}
              <div className="flex items-center justify-center">
                <div> Available Stock</div>
              </div>             
            </div>  
          </div>
          {/* Transfer Items Rows */}
          {adjustmentItems.map((item, index) => {
          // Find the selected product in the product list
          const selectedProduct = products.find((product) => product._id === item.product_id);
          return (
            <div key={index} className="grid grid-cols-2 gap-8 mb-4">
              {/* Product Selection */}
              <div className="w-full">
                {customProductSelect(products, index)}
              </div>
              <div className="grid grid-cols-2 gap-8 mb-4">
                {/* Quantity Input with Unit */}
                <div className="w-full flex items-center">
                  <input
                    type="number"
                    placeholder="Qty"
                    className="p-3 border rounded-lg w-full"
                    value={item.qty}
                    onChange={(e) => handleQuantityChange(item.product_id, Number(e.target.value))}
                    min="0"
                    step={products.find((p) => p._id === item.product_id)?.partial_qty_allocation ? "0.25" : "1"} // Dynamic step size
                  />
                  <span className="ml-2 text-gray-700 font-medium">
                    {selectedProduct?.unit?.unit_name || "N/A"}
                  </span>
                </div>
                {/* Available Stock with Unit */}
                <div className="w-full flex items-center justify-center">
                  <span className="text-gray-700 font-medium">
                    {selectedProduct ? selectedProduct.updated_stock_qty : "N/A"}
                  </span>
                  <span className="ml-2 text-gray-700 font-medium">
                    {selectedProduct?.unit?.unit_name || ""}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
          {/* Add Item Button
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            onClick={handleAddAdjustmentItem}
            >
              Add Item
          </button> */}
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Reason</label>
          <textarea
            className="w-full p-3 border rounded-lg"
            rows="4"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for stock adjustment ......"
          ></textarea>
        </div>
        <div className="text-right">
          <button
            onClick={handleSubmit}
            className="bg-blue-700 text-white py-3 px-6 rounded-lg hover:bg-blue-600"
          >
            Submit Stock Adjustment
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddStockAdjustment;
